import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  BAND_DEFS,
  FLIGHT_RISKS,
  IMPACT_LABELS,
  POTENTIALS,
  POTENTIAL_DEFS,
  POTENTIAL_MAP,
  RISK_MAP,
  boxOf,
  type Potential,
  type Risk,
  type TalentPerformance,
} from '@goyone/shared';
import {
  Badge,
  EmptyState,
  ErrorState,
  Modal,
  Notice,
  PageHead,
  Stat,
} from '../../components/ui';
import { Icon } from '../../components/Icon';
import { useToast } from '../../components/Toast';
import { api } from '../../lib/api';
import { date, num } from '../../lib/format';
import type { AssessmentRow, TalentReviewRow } from './Talent';

interface ReviewView extends TalentReviewRow {
  assessments: AssessmentRow[];
  grid: { key: string; count: number }[];
  highPotential: number;
  atRisk: number;
}

interface Brief {
  suggested: string | null;
  lastReview: {
    cycle: string;
    overall: number | null;
    scaleMax: number;
    bandLabel: string | null;
    endsOn: string | null;
  } | null;
  successorFor: { positionId: string; title: string; readiness: string }[];
  leadership: { courseId: string; title: string; endsOn: string | null }[];
}

interface EmployeeLite {
  id: string;
  name: string;
  jobTitle?: string | null;
}

/** محورُ الأداء في الشبكة — «دون المطلوب» صفٌّ واحدٌ في أسفلها. */
const PERFORMANCE_ROWS: readonly TalentPerformance[] = [
  'TOP',
  'STRONG',
  'MEETS',
] as const;

/**
 * جلسةُ مراجعةِ مواهب.
 *
 * ## الشبكةُ محورها الأداءُ والإمكانات
 *
 * ويُقرأ الصفُّ الأسفلُ وحدَه: «دون المطلوب» خانةٌ واحدةٌ مهما كانت
 * الإمكانات — من لا يؤدّي اليوم لا يُخطَّط لما فوقه قبل أن يؤدّي. وتسعُ
 * خاناتٍ تُنتج ثلاثةَ أسماءٍ لحالةٍ واحدة يقف أمامها من يقرأ.
 *
 * ## والاعتمادُ في اتّجاهٍ واحد
 *
 * ما اعتُمد قرارٌ أُبلغ به، ومن جدّ فيه جديدٌ تُعقد له جلسةٌ أخرى — وقيمةُ
 * المقارنة بين جلستين في أن تبقى الأولى كما كانت.
 */
export function TalentReview() {
  const { id = '' } = useParams();
  const queryClient = useQueryClient();
  const toast = useToast();

  const [assessing, setAssessing] = useState<AssessmentRow | null>(null);
  const [adding, setAdding] = useState(false);
  const [finalizing, setFinalizing] = useState(false);

  const item = useQuery({
    queryKey: ['talent', 'review', id],
    queryFn: () => api<ReviewView>(`/hr/talent/reviews/${id}`),
  });

  const finalize = useMutation({
    mutationFn: () =>
      api(`/hr/talent/reviews/${id}/status`, {
        method: 'POST',
        body: { status: 'FINAL' },
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['talent'] });
      setFinalizing(false);
    },
    onError: (e) => toast(e instanceof Error ? e.message : 'تعذّر الحفظ'),
  });

  const r = item.data;
  const placed = new Map<string, AssessmentRow[]>();
  for (const a of r?.assessments ?? []) {
    const list = placed.get(`${a.performance}|${a.potential}`) ?? [];
    list.push(a);
    placed.set(`${a.performance}|${a.potential}`, list);
  }
  const under = (r?.assessments ?? []).filter((a) => a.performance === 'BELOW');

  return (
    <div className="page">
      <PageHead
        title={r?.name ?? 'جلسة مواهب'}
        subtitle={r ? `عُقدت ${date(r.heldOn ?? '')}` : undefined}
        actions={
          r &&
          r.writable && (
            <div className="row" style={{ gap: 6 }}>
              <button className="btn" onClick={() => setAdding(true)}>
                <Icon name="plus" size={14} />
                ضَع اسمًا
              </button>
              <button
                className="btn btn-primary"
                onClick={() => setFinalizing(true)}
              >
                <Icon name="check" size={14} />
                اعتمِدها
              </button>
            </div>
          )
        }
      />

      <Link className="link tiny" to="/hr/talent">
        <Icon name="arrowLeft" size={12} /> إدارة المواهب
      </Link>

      {item.isError && (
        <ErrorState error={item.error} onRetry={() => void item.refetch()} />
      )}
      {item.isPending && <div className="skeleton" style={{ height: 220 }} />}

      {r && (
        <>
          <div className="stats mt-3">
            <Stat
              label="الحالة"
              value={<Badge kind={r.statusKind}>{r.statusLabel}</Badge>}
              note={r.statusNote}
            />
            <Stat label="الأسماء" value={num(r.assessed)} />
            <Stat
              label="إمكاناتٌ عالية"
              value={num(r.highPotential)}
              note="يُعدّون لما فوق موضعهم"
            />
            <Stat
              label="في خطر"
              value={num(r.atRisk)}
              note="عاليةٌ، واحتمالُ تركٍ مرتفع، وفقدُه يُعطّل"
            />
          </div>

          {r.notes && (
            <Notice title="عن الجلسة" icon="note">
              {r.notes}
            </Notice>
          )}

          {r.assessments.length === 0 && (
            <EmptyState
              title="لا أسماء"
              text="ضَع كلَّ من يُتحدَّث عنه في المصفوفة — ويُقترَح لكلٍّ أداؤه من آخر تقييمٍ أُرسل له."
              action={
                r.writable ? (
                  <button
                    className="btn btn-primary"
                    onClick={() => setAdding(true)}
                  >
                    ضَع أوّلَ اسم
                  </button>
                ) : undefined
              }
            />
          )}

          {/* ---------- الشبكة ---------- */}
          {r.assessments.length > 0 && (
            <div className="card mt-3">
              <h2 className="card-title">المصفوفة</h2>
              <div className="table-wrap mt-2">
                <table className="table">
                  <thead>
                    <tr>
                      <th>الأداء \ الإمكانات</th>
                      {POTENTIAL_DEFS.map((p) => (
                        <th key={p.key}>{p.label}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {PERFORMANCE_ROWS.map((perf) => (
                      <tr key={perf}>
                        <th scope="row">
                          {BAND_DEFS.find((b) => b.key === perf)?.label ?? perf}
                        </th>
                        {POTENTIAL_DEFS.map((p) => {
                          const cell = placed.get(`${perf}|${p.key}`) ?? [];
                          const box = boxOf(perf, p.key);
                          return (
                            <td key={p.key} style={{ verticalAlign: 'top' }}>
                              <div className="tiny mute">{box.label}</div>
                              {cell.map((a) => (
                                <div key={a.id}>
                                  <button
                                    className="link"
                                    type="button"
                                    onClick={() => setAssessing(a)}
                                  >
                                    {a.employee.name}
                                  </button>
                                  {a.atRisk && (
                                    <span
                                      className="tiny"
                                      style={{ color: 'var(--danger)' }}
                                    >
                                      {' '}
                                      ⚑
                                    </span>
                                  )}
                                </div>
                              ))}
                              {cell.length === 0 && (
                                <div className="tiny mute">—</div>
                              )}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/*
                «دون المطلوب» صفٌّ واحدٌ مهما كانت الإمكانات: من لا يؤدّي
                اليوم لا يُخطَّط لما فوقه قبل أن يؤدّي.
              */}
              <div className="mt-3">
                <h3 className="card-title">دون المطلوب</h3>
                <p className="tiny mute">
                  خانةٌ واحدةٌ مهما كانت الإمكانات: من لا يؤدّي اليوم لا يُخطَّط
                  لما فوقه قبل أن يؤدّي. وما يُفعل هنا خطّةُ تحسينٍ بمدّةٍ
                  وأهدافٍ مكتوبة.{' '}
                  <Link className="link" to="/hr/performance">
                    إدارة الأداء
                  </Link>
                  .
                </p>
                <div className="mt-2">
                  {under.length === 0 ? (
                    <span className="tiny mute">لا أحد.</span>
                  ) : (
                    under.map((a) => (
                      <button
                        key={a.id}
                        className="link"
                        type="button"
                        style={{ marginInlineEnd: 12 }}
                        onClick={() => setAssessing(a)}
                      >
                        {a.employee.name}
                      </button>
                    ))
                  )}
                </div>
              </div>
            </div>
          )}

          {/* ---------- الجدول ---------- */}
          {r.assessments.length > 0 && (
            <div className="card mt-3">
              <h2 className="card-title">ما قُرِّر في كلّ اسم</h2>
              <div className="table-wrap sticky-head mt-2">
                <table className="table">
                  <thead>
                    <tr>
                      <th>الاسم</th>
                      <th>الأداء</th>
                      <th>الإمكانات</th>
                      <th>الخانة</th>
                      <th>احتمال الترك</th>
                      <th>أثرُ فقده</th>
                      <th>ما يُفعل</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {r.assessments.map((a) => (
                      <tr key={a.id}>
                        <td>
                          <Link
                            className="link"
                            to={`/hr/employees/${a.employee.id}`}
                          >
                            {a.employee.name}
                          </Link>
                          {a.employee.jobTitle && (
                            <div className="tiny mute">
                              {a.employee.jobTitle}
                            </div>
                          )}
                        </td>
                        <td className="tiny">{a.performanceLabel}</td>
                        <td>
                          <Badge kind={a.potentialKind}>
                            {a.potentialLabel}
                          </Badge>
                        </td>
                        <td>
                          <Badge kind={a.boxKind}>{a.boxLabel}</Badge>
                        </td>
                        <td>
                          <Badge kind={a.riskKind}>{a.riskLabel}</Badge>
                        </td>
                        <td className="tiny">{a.impactLabel}</td>
                        <td className="tiny">{a.actions ?? a.boxAction}</td>
                        <td>
                          {r.writable && (
                            <button
                              className="btn btn-sm"
                              onClick={() => setAssessing(a)}
                            >
                              عدّله
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}

      {(adding || assessing) && (
        <AssessDialog
          reviewId={id}
          current={assessing}
          onClose={() => {
            setAdding(false);
            setAssessing(null);
          }}
        />
      )}

      {finalizing && (
        <Modal
          title="اعتماد الجلسة"
          onClose={() => setFinalizing(false)}
          footer={
            <>
              <button className="btn" onClick={() => setFinalizing(false)}>
                إلغاء
              </button>
              <button
                className="btn btn-primary"
                disabled={finalize.isPending}
                onClick={() => finalize.mutate()}
              >
                اعتمِدها
              </button>
            </>
          }
        >
          <Notice title="ولا رجعةَ بعده" icon="alert" kind="warning">
            ما فيها يصير قرارَ الجلسة ولا يُعدَّل. ومن جدّ فيه جديدٌ تُعقد له
            جلسةٌ أخرى — وقيمةُ المقارنة بين جلستين في أن تبقى الأولى كما كانت.
            و«أصحابُ الإمكانات العالية» في النظام يُقرؤون من آخر جلسةٍ اعتُمدت.
          </Notice>
        </Modal>
      )}
    </div>
  );
}

/* ==========================================================================
   وضعُ اسمٍ في المصفوفة
   ========================================================================== */

function AssessDialog({
  reviewId,
  current,
  onClose,
}: {
  reviewId: string;
  current: AssessmentRow | null;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const toast = useToast();

  const [employeeId, setEmployeeId] = useState(current?.employee.id ?? '');
  const [performance, setPerformance] = useState<TalentPerformance>(
    (current?.performance as TalentPerformance) ?? 'MEETS',
  );
  const [potential, setPotential] = useState<Potential>(
    current?.potential ?? 'MEDIUM',
  );
  const [flightRisk, setFlightRisk] = useState<Risk>(
    current?.flightRisk ?? 'LOW',
  );
  const [impactOfLoss, setImpactOfLoss] = useState(
    String(current?.impactOfLoss ?? 2),
  );
  const [actions, setActions] = useState(current?.actions ?? '');
  const [notes, setNotes] = useState(current?.notes ?? '');

  const staff = useQuery({
    queryKey: ['employees', { status: 'ACTIVE', pageSize: 200 }],
    queryFn: () =>
      api<{ items: EmployeeLite[] }>(
        '/hr/employees?status=ACTIVE&pageSize=200',
      ),
    enabled: current === null,
  });

  /*
   * ما يُقال عن الاسم قبل أن يُوضَع.
   *
   * آخرُ تقييمٍ أُرسل له، وهل هو مرشَّحُ خلافةٍ لأحد، وهل حضر برنامجَ
   * قيادة. وجمعُ هذه من ثلاث شاشاتٍ بيدٍ هو ما يجعل الجلساتِ تُعقد على
   * الانطباع.
   */
  const brief = useQuery({
    queryKey: ['talent', 'brief', employeeId],
    queryFn: () => api<Brief>(`/hr/talent/brief/${employeeId}`),
    enabled: Boolean(employeeId),
  });

  const suggested = brief.data?.suggested ?? null;
  const box = boxOf(performance, potential);

  const save = useMutation({
    mutationFn: () =>
      api(`/hr/talent/reviews/${reviewId}/assess`, {
        method: 'POST',
        body: {
          employeeId,
          performance,
          potential,
          flightRisk,
          impactOfLoss: Number(impactOfLoss),
          actions: actions || null,
          notes: notes || null,
        },
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['talent'] });
      toast('حُفظ.');
      onClose();
    },
    onError: (e) => toast(e instanceof Error ? e.message : 'تعذّر الحفظ'),
  });

  return (
    <Modal
      title={
        current ? `موضع ${current.employee.name}` : 'ضَع اسمًا في المصفوفة'
      }
      onClose={onClose}
      wide
      footer={
        <>
          <button className="btn" onClick={onClose}>
            إلغاء
          </button>
          <button
            className="btn btn-primary"
            disabled={save.isPending || !employeeId}
            onClick={() => save.mutate()}
          >
            احفظ
          </button>
        </>
      }
    >
      <div className="form-grid">
        {current === null && (
          <div className="field span-2">
            <label className="label req" htmlFor="ta-emp">
              الموظّف
            </label>
            <select
              id="ta-emp"
              className="select"
              value={employeeId}
              onChange={(e) => setEmployeeId(e.target.value)}
            >
              <option value="">— اختر —</option>
              {(staff.data?.items ?? []).map((e) => (
                <option key={e.id} value={e.id}>
                  {e.name}
                </option>
              ))}
            </select>
          </div>
        )}

        {brief.data && (
          <div className="field span-2">
            <Notice title="ما يُقال عنه اليوم" icon="clipboard">
              {brief.data.lastReview ? (
                <>
                  آخرُ تقييم: «{brief.data.lastReview.cycle}» —{' '}
                  {num(brief.data.lastReview.overall ?? 0)} من{' '}
                  {num(brief.data.lastReview.scaleMax)}
                  {brief.data.lastReview.bandLabel
                    ? ` (${brief.data.lastReview.bandLabel})`
                    : ''}
                  .{' '}
                </>
              ) : (
                <>لا تقييمَ أُرسل له بعد. </>
              )}
              {brief.data.successorFor.length > 0 && (
                <>
                  ومرشَّحُ خلافةٍ لـ
                  {brief.data.successorFor.map((s) => s.title).join('، ')}.{' '}
                </>
              )}
              {brief.data.leadership.length > 0 ? (
                <>
                  وحضر من برامج القيادة:{' '}
                  {brief.data.leadership.map((l) => l.title).join('، ')}.
                </>
              ) : (
                <>ولم يحضر برنامجَ قيادةٍ بعد.</>
              )}
            </Notice>
          </div>
        )}

        <div className="field">
          <label className="label" htmlFor="ta-perf">
            الأداء
          </label>
          <select
            id="ta-perf"
            className="select"
            value={performance}
            onChange={(e) =>
              setPerformance(e.target.value as TalentPerformance)
            }
          >
            {BAND_DEFS.map((b) => (
              <option key={b.key} value={b.key}>
                {b.label}
              </option>
            ))}
          </select>
          {/*
            يُقترَح ولا يُفرض: الجلسةُ قد ترى غيرَ ما في التقييم، والمهمُّ
            أن يُكتب ما رأته هي. ويُثبَّت هنا فتبقى الجلسةُ قابلةً للقراءة
            بعد دورتين.
          */}
          {suggested && suggested !== performance && (
            <span className="tiny mute">
              المقترَحُ من آخر تقييم:{' '}
              {BAND_DEFS.find((b) => b.key === suggested)?.label}{' '}
              <button
                className="link"
                type="button"
                onClick={() => setPerformance(suggested as TalentPerformance)}
              >
                خذه
              </button>
            </span>
          )}
        </div>

        <div className="field">
          <label className="label" htmlFor="ta-pot">
            الإمكانات
          </label>
          <select
            id="ta-pot"
            className="select"
            value={potential}
            onChange={(e) => setPotential(e.target.value as Potential)}
          >
            {POTENTIALS.map((k) => (
              <option key={k} value={k}>
                {POTENTIAL_MAP[k].label}
              </option>
            ))}
          </select>
          <span className="tiny mute">
            {POTENTIAL_MAP[potential].description}
          </span>
        </div>

        <div className="field">
          <label className="label" htmlFor="ta-risk">
            احتمال الترك
          </label>
          <select
            id="ta-risk"
            className="select"
            value={flightRisk}
            onChange={(e) => setFlightRisk(e.target.value as Risk)}
          >
            {FLIGHT_RISKS.map((k) => (
              <option key={k} value={k}>
                {RISK_MAP[k].label}
              </option>
            ))}
          </select>
          <span className="tiny mute">{RISK_MAP[flightRisk].description}</span>
        </div>

        <div className="field">
          <label className="label" htmlFor="ta-impact">
            أثرُ فقده
          </label>
          <select
            id="ta-impact"
            className="select"
            value={impactOfLoss}
            onChange={(e) => setImpactOfLoss(e.target.value)}
          >
            {[1, 2, 3].map((k) => (
              <option key={k} value={String(k)}>
                {IMPACT_LABELS[k]}
              </option>
            ))}
          </select>
        </div>

        <div className="field span-2">
          <Notice title={`الخانة: ${box.label}`} icon="target">
            {box.action}
          </Notice>
        </div>

        <div className="field span-2">
          <label className="label" htmlFor="ta-actions">
            ما تقرّر أن يُفعل به
          </label>
          <textarea
            id="ta-actions"
            className="input"
            rows={2}
            value={actions}
            onChange={(e) => setActions(e.target.value)}
            placeholder={box.action}
          />
          <span className="tiny mute">
            يُسجَّل هنا ويُنفَّذ في بابه: الترقيةُ في ملفّ الموظّف، والتدريبُ في
            التدريب، والخلافةُ في خطط الإحلال.
          </span>
        </div>

        <div className="field span-2">
          <label className="label" htmlFor="ta-notes">
            ملاحظات
          </label>
          <textarea
            id="ta-notes"
            className="input"
            rows={2}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
        </div>
      </div>
    </Modal>
  );
}
